import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mic, Mail, Lock, Eye, EyeOff, LogIn } from 'lucide-react';
import GlowButton from '../components/ui/GlowButton';
import './Auth.css';

export default function Login() {
  const { signIn, signInWithGoogle, DEV_MODE, hasActivePlan } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const redirectAfterLogin = () => {
    if (returnTo) {
      navigate(returnTo);
    } else if (hasActivePlan()) {
      navigate('/dashboard');
    } else {
      navigate('/pricing');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    const { error } = await signIn(email, password);
    setLoading(false);

    if (error) {
      setErrorMsg(error);
    } else {
      redirectAfterLogin();
    }
  };

  const handleGoogle = async () => {
    setLoading(true);
    setErrorMsg('');
    const { error } = await signInWithGoogle();
    setLoading(false);

    if (error) {
      setErrorMsg(error);
    } else {
      redirectAfterLogin();
    }
  };

  return (
    <div className="auth-page">
      {/* Background Orbs */}
      <div className="auth-page__orb auth-page__orb--1" />
      <div className="auth-page__orb auth-page__orb--2" />

      <div className="auth-container">
        {/* Logo centrado afuera de la card con proporciones de landing */}
        <Link to="/" className="auth-logo">
          <div className="auth-logo-icon">
            <Mic className="w-5 h-5 text-white" />
          </div>
          <span className="auth-logo-title text-gradient">Garaoke</span>
          <span className="auth-logo-version">v2.4.0</span>
        </Link>

        {/* Card */}
        <div className="auth-card glass">
          <h1 className="auth-title">Iniciar Sesión</h1>
          <p className="auth-subtitle">Ingresá a tu cuenta para gestionar tu karaoke</p>

          {errorMsg && (
            <div className="auth-alert">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-input-group">
              <Mail className="auth-icon-left w-5 h-5" />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="auth-input"
                required
              />
            </div>

            <div className="auth-input-group">
              <Lock className="auth-icon-left w-5 h-5" />
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input auth-input--has-right"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="auth-toggle-pw"
              >
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="glow-btn glow-btn--primary glow-btn--full auth-btn-submit"
            >
              {loading ? (
                <div className="auth-spinner" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Ingresar</span>
                </>
              )}
            </button>
          </form>

          <div className="auth-divider">
            <div className="auth-divider-line" />
            <span className="auth-divider-text">O continuar con</span>
            <div className="auth-divider-line" />
          </div>

          <button
            onClick={handleGoogle}
            className="glow-btn glow-btn--secondary glow-btn--full auth-google-btn"
            type="button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Google</span>
          </button>

          {DEV_MODE && (
            <div className="auth-dev-wrap">
              <button onClick={handleGoogle} className="auth-dev-btn" type="button">
                ⚡ Entrar en Modo Dev (Bypass Rápido)
              </button>
            </div>
          )}

          <p className="auth-footer-text">
            ¿No tenés cuenta?{' '}
            <Link to={`/register?returnTo=${encodeURIComponent(returnTo || '/pricing')}`} className="auth-link">
              Registrate gratis
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
